import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  Business,
  Work,
  Gavel,
  Star,
  Refresh,
  TrendingUp,
  People,
  Search,
  LocationOn,
  Warning,
  SwapHoriz,
  Receipt,
  Cancel,
  VerifiedUser,
  Payment,
  Savings,
  Groups,
  SmartToyOutlined,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import ProviderDetailDialog from '../components/ProviderDetailDialog';
import CustomerSearchDialog from '../components/CustomerSearchDialog';
import CustomerProfileDialog from '../components/CustomerProfileDialog';
import NonServicedZipDialog from '../components/NonServicedZipDialog';

interface ProviderSummary {
  _id: string;
  businessName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  rating: number;
  currentJobs: number;
  totalCustomers: number;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
}

interface AdminStats {
  totalProviders: number;
  activeProviders: number;
  totalJobs: number;
  pendingBids: number;
  totalRevenue: number;
  averageRating: number;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState<ProviderSummary[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [providerDetailDialog, setProviderDetailDialog] = useState({
    open: false,
    providerId: '',
    providerName: ''
  });
  const [customerSearchDialog, setCustomerSearchDialog] = useState({
    open: false
  });
  const [customerProfileDialog, setCustomerProfileDialog] = useState({
    open: false,
    customerId: ''
  });
  const [customerSearchDialogRefresh, setCustomerSearchDialogRefresh] = useState(0);
  const [nonServicedZipDialog, setNonServicedZipDialog] = useState({
    open: false
  });
  const [serviceRemovalRequests, setServiceRemovalRequests] = useState<any[]>([]);
  const [loadingRemovalRequests, setLoadingRemovalRequests] = useState(false);
  const [serviceTransferDialog, setServiceTransferDialog] = useState({
    open: false,
    serviceRequestId: '',
    customerName: '',
    serviceType: '',
    currentProvider: '',
    bids: [] as any[],
    isImportedCustomer: false,
    currentFeePercentage: 15,
  });
  const [transferFeePercentage, setTransferFeePercentage] = useState(1);
  const [verificationFilter, setVerificationFilter] = useState<'all' | 'unverified'>('all');
  const [routeEmailBusy, setRouteEmailBusy] = useState(false);
  const [routeEmailMsg, setRouteEmailMsg] = useState('');

  const checkDailyRouteEmailStatus = async () => {
    setRouteEmailBusy(true);
    setRouteEmailMsg('');
    try {
      const res = await apiService.get('/admin/daily-route-email-status');
      const cfg = (res as any)?.configuration || {};
      const summary = (res as any)?.summary || {};
      const dateRange = (res as any)?.dateRange || {};
      setRouteEmailMsg(
        `Cron: ${cfg.cronEnabled ? 'enabled' : 'DISABLED'} (${cfg.cronSchedule || 'n/a'}). ` +
          `Tomorrow ${dateRange.tomorrow || '?'}: ${summary.providersWithServices || 0} provider(s) with services, ` +
          `${summary.totalServices || 0} service(s). SendGrid: ${cfg.sendgridApiKey || '?'}.`
      );
    } catch (err: any) {
      setRouteEmailMsg(err?.message || 'Failed to load daily route email status');
    } finally {
      setRouteEmailBusy(false);
    }
  };

  const triggerDailyRouteEmail = async () => {
    setRouteEmailBusy(true);
    setRouteEmailMsg('');
    try {
      const res = await apiService.post('/admin/trigger-daily-route-email', {});
      const data = (res as any)?.data || {};
      const results = (data.results || []) as Array<{
        provider?: string;
        email?: string;
        status?: string;
        reason?: string;
        servicesCount?: number;
      }>;
      const failures = results.filter((r) => r.status === 'failed' || r.status === 'error');
      const failureDetail = failures
        .slice(0, 5)
        .map((r) => `${r.provider || r.email}: ${r.reason || r.status}`)
        .join(' | ');
      setRouteEmailMsg(
        `${(res as any)?.message || `Sent: ${data.emailsSent ?? 0}, Failed: ${data.emailsFailed ?? 0}`}` +
          (failureDetail ? ` — ${failureDetail}` : '')
      );
    } catch (err: any) {
      const raw = err?.raw || {};
      const data = raw.data || {};
      const results = (data.results || []) as Array<{
        provider?: string;
        email?: string;
        status?: string;
        reason?: string;
      }>;
      const failures = results.filter((r) => r.status === 'failed' || r.status === 'error');
      const failureDetail = failures
        .slice(0, 5)
        .map((r) => `${r.provider || r.email}: ${r.reason || r.status}`)
        .join(' | ');
      setRouteEmailMsg(
        (err?.message || 'Failed to trigger daily route email') +
          (failureDetail ? ` — ${failureDetail}` : '')
      );
    } finally {
      setRouteEmailBusy(false);
    }
  };

  useEffect(() => {
    fetchProviderSummary();
    fetchServiceRemovalRequests();
  }, []);

  const fetchProviderSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/admin/providers-summary');
      
      if (response.success && response.data) {
        setProviders((response.data as any).providers || []);
        setStats((response.data as any).stats);
      } else {
        setError('Failed to load provider summary');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load provider summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceRemovalRequests = async () => {
    try {
      setLoadingRemovalRequests(true);
      const response = await apiService.get('/admin/service-removal-requests');
      
      if (response.success && response.data) {
        setServiceRemovalRequests((response.data as any).removalRequests || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch service removal requests:', err);
    } finally {
      setLoadingRemovalRequests(false);
    }
  };

  const handleViewBidsAndTransfer = async (serviceRequestId: string, customerName: string, serviceType: string, currentProvider: string) => {
    try {
      const response = await apiService.get(`/admin/service-requests/${serviceRequestId}/bids`);
      
      if (response.success && response.data) {
        const data = response.data as any;
        const serviceRequest = data.serviceRequest;
        const isImported = serviceRequest?.isImportedCustomer || false;
        const acceptedBid = data.acceptedBid;
        const currentFee = acceptedBid?.platformFeePercentage || 
                          data.bids?.find((b: any) => b.status === 'accepted')?.platformFeePercentage || 5;
        
        setServiceTransferDialog({
          open: true,
          serviceRequestId,
          customerName,
          serviceType,
          currentProvider,
          bids: data.bids || [],
          isImportedCustomer: isImported,
          currentFeePercentage: currentFee,
        });
        
        // Set default fee: 1% for onboarded customers, 1% as incentive for transfers
        setTransferFeePercentage(1);
      } else {
        setError('Failed to load bids for this service request');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load bids');
    }
  };

  const handleTransferService = async (newProviderId: string, bidId?: string) => {
    try {
      // For onboarded customers, fee is automatically 1% (can't be changed)
      // For new customers, use the selected fee percentage (default 1% as incentive)
      const feePercentage = serviceTransferDialog.isImportedCustomer ? 1 : transferFeePercentage;
      
      const response = await apiService.post(`/admin/service-requests/${serviceTransferDialog.serviceRequestId}/transfer`, {
        newProviderId,
        bidId,
        customFeePercentage: feePercentage,
      });
      
      if (response.success) {
        setServiceTransferDialog({ open: false, serviceRequestId: '', customerName: '', serviceType: '', currentProvider: '', bids: [], isImportedCustomer: false, currentFeePercentage: 5 });
        setTransferFeePercentage(1);
        fetchServiceRemovalRequests();
        fetchProviderSummary();
        setError('');
        // Show success message
        alert(`Service successfully transferred! Platform fee set to ${feePercentage}% (${feePercentage === 10 && !serviceTransferDialog.isImportedCustomer ? 'incentive rate' : serviceTransferDialog.isImportedCustomer ? 'onboarded customer rate' : 'standard rate'}).`);
      } else {
        setError(response.error || response.message || 'Failed to transfer service');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to transfer service');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const getVerificationColor = (isVerified: boolean) => {
    return isVerified ? 'success' : 'warning';
  };

  const handleProviderClick = (provider: ProviderSummary) => {
    setProviderDetailDialog({
      open: true,
      providerId: provider._id ? String(provider._id) : '',
      providerName: provider.businessName || `${provider.firstName} ${provider.lastName}`
    });
  };

  const handleCloseProviderDialog = () => {
    setProviderDetailDialog({
      open: false,
      providerId: '',
      providerName: ''
    });
  };

  const handleOpenCustomerSearch = () => {
    setCustomerSearchDialog({ open: true });
  };

  const handleCloseCustomerSearch = () => {
    setCustomerSearchDialog({ open: false });
  };

  const handleViewCustomerProfile = (customerId: string) => {
    setCustomerProfileDialog({
      open: true,
      customerId: customerId
    });
    setCustomerSearchDialog({ open: false });
  };

  const handleCloseCustomerProfile = () => {
    setCustomerProfileDialog({
      open: false,
      customerId: ''
    });
  };

  const handleCustomerDeleted = () => {
    // Trigger a refresh of the customer search dialog
    setCustomerSearchDialogRefresh(prev => prev + 1);
  };

  const handleOpenNonServicedZipDialog = () => {
    setNonServicedZipDialog({ open: true });
  };

  const handleCloseNonServicedZipDialog = () => {
    setNonServicedZipDialog({ open: false });
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Platform overview and provider management
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleOpenCustomerSearch}
          >
            Search Customers
          </Button>
          <Button
            variant="contained"
            startIcon={<LocationOn />}
            onClick={handleOpenNonServicedZipDialog}
            color="warning"
          >
            Non-Service Areas
          </Button>
          <Button
            variant="contained"
            startIcon={<Cancel />}
            onClick={() => navigate('/admin/cancellations')}
            color="info"
          >
            Cancellations
          </Button>
          <Button
            variant="contained"
            startIcon={<Payment />}
            onClick={() => navigate('/admin/payments-requiring-attention')}
            color="error"
          >
            Payments Requiring Attention
          </Button>
          <Button
            variant={verificationFilter === 'unverified' ? 'contained' : 'outlined'}
            startIcon={<VerifiedUser />}
            onClick={() => setVerificationFilter(verificationFilter === 'unverified' ? 'all' : 'unverified')}
            color={verificationFilter === 'unverified' ? 'warning' : 'inherit'}
          >
            {verificationFilter === 'unverified' ? 'Show All Providers' : 'Provider Verification'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchProviderSummary}
            disabled={loading}
          >
            Refresh Data
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <People sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" color="primary">
                  {stats.totalProviders}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Providers
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Business sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" color="success.main">
                  {stats.activeProviders}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Providers
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Work sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="h4" color="info.main">
                  {stats.totalJobs}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Jobs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Gavel sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {stats.pendingBids}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Bids
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" color="success.main">
                  ${stats.totalRevenue.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Star sx={{ fontSize: 40, color: '#ffc107', mb: 1 }} />
                <Typography variant="h4" sx={{ color: '#ffc107' }}>
                  {stats.averageRating.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Rating
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Daily route backup email ops */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Provider backup route email (night before)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Sends tomorrow&apos;s schedule to providers (~10 PM Central). Railway in-process cron can miss
          if the service slept — use Check / Send now to verify, and keep cron-job.org configured with
          CRON_API_KEY.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: routeEmailMsg ? 1.5 : 0 }}>
          <Button variant="outlined" disabled={routeEmailBusy} onClick={checkDailyRouteEmailStatus}>
            {routeEmailBusy ? 'Working…' : 'Check status'}
          </Button>
          <Button variant="contained" disabled={routeEmailBusy} onClick={triggerDailyRouteEmail}>
            {routeEmailBusy ? 'Sending…' : 'Send backup emails now'}
          </Button>
        </Box>
        {routeEmailMsg && (
          <Alert severity={routeEmailMsg.toLowerCase().includes('fail') || routeEmailMsg.toLowerCase().includes('disabled') ? 'warning' : 'info'}>
            {routeEmailMsg}
          </Alert>
        )}
      </Paper>

      {/* Quick Actions */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }} onClick={() => navigate('/admin/services/search')}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Receipt sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Service Search</Typography>
                <Typography variant="body2" color="text.secondary">
                  Legal & Tax Records
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
              onClick={() => navigate('/admin/provider-savings-leads')}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <Savings sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Savings Leads</Typography>
                <Typography variant="body2" color="text.secondary">
                  Homepage calculator emails
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
              onClick={() => navigate('/admin/demo-requests')}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <Groups sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Demo Requests</Typography>
                <Typography variant="body2" color="text.secondary">
                  Provider sandbox access
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }} onClick={() => navigate('/admin/agents')}>
              <CardContent sx={{ textAlign: 'center' }}>
                <SmartToyOutlined sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">AI Agents</Typography>
                <Typography variant="body2" color="text.secondary">
                  Pilot activity & feedback
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Providers Summary Table */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business sx={{ fontSize: 24, color: 'primary.main' }} />
              <Typography variant="h5" component="h2">
                Providers Summary
              </Typography>
              {verificationFilter === 'unverified' && (
                <Chip 
                  label={`Showing ${providers.filter(p => !p.isVerified).length} Unverified`} 
                  color="warning" 
                  size="small"
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
          </Box>

          {providers.length === 0 ? (
            <Alert severity="info">
              No providers found. Providers will appear here once they register on the platform.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Rating</TableCell>
                    <TableCell align="center">Current Jobs</TableCell>
                    <TableCell align="center">Total Customers</TableCell>
                    <TableCell align="center">Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {providers
                    .filter(provider => verificationFilter === 'all' || !provider.isVerified)
                    .map((provider) => (
                    <TableRow 
                      key={provider._id} 
                      hover 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleProviderClick(provider);
                      }}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {provider.businessName ? provider.businessName[0] : provider.firstName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {provider.businessName || `${provider.firstName} ${provider.lastName}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {provider.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={provider.isActive ? 'Active' : 'Inactive'}
                            color={getStatusColor(provider.isActive) as any}
                            size="small"
                          />
                          <Chip
                            label={provider.isVerified ? 'Verified' : 'Unverified'}
                            color={getVerificationColor(provider.isVerified) as any}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Star sx={{ fontSize: 16, color: '#ffc107' }} />
                          <Typography variant="body2">
                            {provider.rating > 0 ? provider.rating.toFixed(1) : 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="medium">
                          {provider.currentJobs}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="medium">
                          {provider.totalCustomers}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(provider.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Provider Detail Dialog */}
      <ProviderDetailDialog
        open={providerDetailDialog.open}
        onClose={handleCloseProviderDialog}
        providerId={providerDetailDialog.providerId}
        providerName={providerDetailDialog.providerName}
        onVerificationChange={fetchProviderSummary}
      />

      {/* Customer Search Dialog */}
      <CustomerSearchDialog
        open={customerSearchDialog.open}
        onClose={handleCloseCustomerSearch}
        onViewProfile={handleViewCustomerProfile}
      />

      {/* Customer Profile Dialog */}
      <CustomerProfileDialog
        open={customerProfileDialog.open}
        onClose={handleCloseCustomerProfile}
        customerId={customerProfileDialog.customerId}
        onDelete={handleCustomerDeleted}
      />

      {/* Non-Serviced Zip Code Dialog */}
      <NonServicedZipDialog
        open={nonServicedZipDialog.open}
        onClose={handleCloseNonServicedZipDialog}
      />

      {/* Service Removal Requests Section */}
      {serviceRemovalRequests.length > 0 && (
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning sx={{ fontSize: 24, color: 'warning.main' }} />
              <Typography variant="h5" component="h2">
                Service Removal Requests
              </Typography>
              <Chip label={serviceRemovalRequests.length} color="warning" size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Providers who need to remove services but have active customer commitments. Transfer these services to alternative providers.
            </Typography>
            
            {serviceRemovalRequests.map((request, idx) => (
              <Card key={idx} sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">{request.providerName}</Typography>
                      <Typography variant="body2" color="text.secondary">{request.providerEmail}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {request.totalActiveJobs} active job{request.totalActiveJobs !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {Object.entries(request.servicesWithActiveJobs).map(([serviceCategory, jobs]: [string, any]) => (
                    <Box key={serviceCategory} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {serviceCategory === 'mowing' ? 'Lawn Mowing/Trimming' :
                         serviceCategory === 'fertilizing' ? 'Weed Control/Fertilizing' :
                         serviceCategory === 'snow_removal' ? 'Snow Removal' : serviceCategory}
                      </Typography>
                      {(jobs as any[]).map((job: any) => (
                        <Box key={job.serviceRequestId} sx={{ mb: 1, pl: 2, borderLeft: '2px solid', borderColor: 'warning.main' }}>
                          <Typography variant="body2">
                            <strong>Customer:</strong> {job.customerName} ({job.customerEmail})
                          </Typography>
                          <Typography variant="body2">
                            <strong>Address:</strong> {job.address?.street}, {job.address?.city}, {job.address?.state} {job.address?.zipCode}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Status:</strong> {job.status}
                          </Typography>
                          {job.scheduledDate && (
                            <Typography variant="body2">
                              <strong>Scheduled:</strong> {new Date(job.scheduledDate).toLocaleDateString()}
                            </Typography>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SwapHoriz />}
                            onClick={() => handleViewBidsAndTransfer(
                              job.serviceRequestId,
                              job.customerName,
                              job.serviceType?.name || serviceCategory,
                              request.providerName
                            )}
                            sx={{ mt: 1 }}
                          >
                            View Bids & Transfer
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Service Transfer Dialog */}
      <Dialog
        open={serviceTransferDialog.open}
        onClose={() => {
          setServiceTransferDialog({ open: false, serviceRequestId: '', customerName: '', serviceType: '', currentProvider: '', bids: [], isImportedCustomer: false, currentFeePercentage: 15 });
          setTransferFeePercentage(10);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Transfer Service: {serviceTransferDialog.serviceType}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Customer:</strong> {serviceTransferDialog.customerName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Current Provider:</strong> {serviceTransferDialog.currentProvider}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Customer Type:</strong> {serviceTransferDialog.isImportedCustomer ? 'Onboarded Customer (1% fee)' : 'New Platform Customer'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Current Platform Fee:</strong> {serviceTransferDialog.currentFeePercentage}%
            </Typography>
          </Box>
          
          {!serviceTransferDialog.isImportedCustomer && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
              <Typography variant="subtitle2" gutterBottom>
                Platform Fee Override (Incentive for Provider)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reduce the platform fee from 5% to 1% as an incentive for providers taking on transferred customers.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Platform Fee Percentage</InputLabel>
                <Select
                  value={transferFeePercentage}
                  label="Platform Fee Percentage"
                  onChange={(e) => setTransferFeePercentage(Number(e.target.value))}
                >
                  <MenuItem value={5}>5% (Standard Rate)</MenuItem>
                  <MenuItem value={1}>1% (Incentive Rate - Recommended)</MenuItem>
                </Select>
                <FormHelperText>
                  {transferFeePercentage === 1 
                    ? 'Provider will receive 99% of the service price (incentive for taking transferred customer)'
                    : 'Provider will receive 95% of the service price (standard rate)'}
                </FormHelperText>
              </FormControl>
            </Box>
          )}
          
          {serviceTransferDialog.isImportedCustomer && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This is an onboarded customer. Platform fee will remain at 1% (cannot be changed).
            </Alert>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" gutterBottom>
            Available Providers (Previous Bids)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a provider from those who previously bid on this service request.
          </Typography>
          
          {serviceTransferDialog.bids.length === 0 ? (
            <Alert severity="info">
              No previous bids found for this service request. You'll need to manually assign a provider.
            </Alert>
          ) : (
            <List>
              {serviceTransferDialog.bids
                .filter((bid: any) => bid.status !== 'accepted' || bid.providerId !== serviceTransferDialog.currentProvider)
                .map((bid: any) => (
                  <ListItem key={bid._id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{bid.providerName}</Typography>
                          <Chip
                            label={bid.status}
                            color={bid.status === 'rejected' ? 'default' : bid.status === 'pending' ? 'warning' : 'success'}
                            size="small"
                          />
                          {bid.providerRating > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Star sx={{ fontSize: 16, color: '#ffc107' }} />
                              <Typography variant="body2">{bid.providerRating.toFixed(1)}</Typography>
                            </Box>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            <strong>Price:</strong> ${bid.price?.toFixed(2) || 'N/A'}
                          </Typography>
                          {bid.servicePricing && bid.servicePricing.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {bid.servicePricing.map((sp: any) => `${sp.serviceName}: $${sp.price}`).join(', ')}
                            </Typography>
                          )}
                          <Typography variant="caption" display="block" color="text.secondary">
                            Bid submitted: {new Date(bid.createdAt).toLocaleDateString()}
                          </Typography>
                          {bid.providerServices && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Services: {bid.providerServices.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SwapHoriz />}
                        onClick={() => handleTransferService(bid.providerId, bid._id)}
                        disabled={bid.status === 'accepted' && bid.providerId === serviceTransferDialog.currentProvider}
                      >
                        Transfer
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setServiceTransferDialog({ open: false, serviceRequestId: '', customerName: '', serviceType: '', currentProvider: '', bids: [], isImportedCustomer: false, currentFeePercentage: 15 });
            setTransferFeePercentage(10);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
